/**
 * @fileoverview Slide-out side menu for module navigation and settings links.
 *
 * @purpose Render the mobile/desktop slide-out menu (nav, settings/admin, theme).
 * @usage Toggled by the TopBar when the side menu icon is clicked.
 * @ownership Shared UI components (core)
 * @dependencies
 * - react (useCallback, useEffect, useState)
 * - @/core/components/shared/Icon/ModuleIcon
 * - @korvo/core/hooks (useFocusTrap, useKeyboard)
 * @restrictions
 * - DO NOT render without keyboard trapping when open.
 */
'use client';

import { useFocusTrap } from '@korvo/core/hooks/useFocusTrap';
import { useKeyboard } from '@korvo/core/hooks/useKeyboard';
import { toggleDarkMode } from '@korvo/core/lib/theme';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { AppsHomeLink } from '../AppsHomeLink';
import { ModuleIcon } from '../Icon/ModuleIcon';
import { type ModuleMenuItem, type UserProfile } from './types';

type SideMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  moduleMenuItems: ModuleMenuItem[];
  userProfile: UserProfile;
  onLogout: () => void;
  isMobile: boolean;
  /** Context-aware content (e.g. chat thread list when on /chat). Rendered after nav, before Settings. */
  extraContent?: React.ReactNode;
  /** When true, show dark mode toggle in drawer (e.g. when topbar is minimal). */
  showDarkModeInDrawer?: boolean;
  /** When true, show Home link as the first drawer item with label "Home" (used when topbar hides home and shows sidebar). */
  showHomeAsFirstItem?: boolean;
}

export function SideMenu({
  isOpen,
  onClose,
  moduleMenuItems,
  userProfile,
  onLogout,
  isMobile,
  extraContent,
  showDarkModeInDrawer = false,
  showHomeAsFirstItem = false,
}: SideMenuProps) {
  // Handle keyboard navigation
  useKeyboard({ onEscape: onClose }, isOpen);
  const containerRef = useFocusTrap(isOpen);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const [isDarkMode, setIsDarkMode] = useState(false);
  useEffect(() => {
    if (!showDarkModeInDrawer) return;
    const read = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
    read();
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, [showDarkModeInDrawer]);
  const handleToggleDark = useCallback(() => {
    toggleDarkMode();
    setIsDarkMode((prev) => !prev);
  }, []);

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const SWIPE_THRESHOLD = 60;
  const SWIPE_MAX_VERTICAL = 100;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (!start) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (dx < -SWIPE_THRESHOLD && Math.abs(dy) < SWIPE_MAX_VERTICAL) {
        onClose();
      }
    },
    [onClose]
  );

  const overlayAndPanel = (
    <>
      {/* Overlay — darkened, blurred; click to close (UX_GUIDELINES §7) */}
      <div
        className="fixed inset-0 z-40 cursor-pointer"
        style={{
          backgroundColor: 'var(--drawer-overlay-background)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
        onClick={onClose}
        aria-hidden="true"
        data-testid="drawer-overlay"
      />
      {/* Side Menu Panel — width min(300px, 86vw); swipe left to close */}
      <div
        ref={containerRef}
        className={`side-menu fixed inset-y-0 left-0 shadow-lg z-50 transform transition-transform duration-200 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        data-testid="drawer"
        style={{
          width: 'min(300px, 86vw)',
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          backgroundColor: 'var(--side-menu-background, var(--background-secondary))',
          color: 'var(--foreground-default)',
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header: minimal so more space for nav; close only */}
        <div className="h-12 px-4 flex items-center justify-end border-b" style={{ borderColor: 'var(--border-default)' }}>
          <button
            onClick={onClose}
            className="p-2 rounded transition-colors"
            style={{ color: 'var(--foreground-default)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--panel-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label="Close menu"
            data-testid="menu-close"
          >
            <ModuleIcon icon="ri-close-line" className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 safe-area-y">
          {/* When topbar shows sidebar and hides home: Home as first item with label. Otherwise when no extraContent: primary Home link. */}
          {showHomeAsFirstItem ? (
            <div className="mb-6" data-testid="drawer-home-first">
              <AppsHomeLink showLabel label="Home" onClick={onClose} aria-label="Go to home" />
            </div>
          ) : extraContent == null ? (
            <AppsHomeLink primary onClick={onClose} aria-label="Go to home" />
          ) : null}
          {extraContent != null ? (
            <div className="mb-6" data-testid="drawer-extra-content">
              {extraContent}
            </div>
          ) : null}

          {/* Admin Panel, Help, Developer Docs: per DESIGN_MIGRATION_PLAN Phase 4.2 — accessible from App Home tiles only, not in side menu */}

          {showDarkModeInDrawer && (
            <div className="mb-4 flex items-center justify-between rounded-md px-3 py-2" style={{ backgroundColor: 'var(--background-default)' }}>
              <span className="text-sm" style={{ color: 'var(--foreground-default)' }}>Theme</span>
              <button
                type="button"
                onClick={handleToggleDark}
                className="p-2 rounded transition-colors"
                style={{ color: 'var(--foreground-default)' }}
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                data-testid="drawer-theme-toggle"
              >
                {isDarkMode ? (
                  <ModuleIcon icon="ri-sun-line" className="h-5 w-5" />
                ) : (
                  <ModuleIcon icon="ri-moon-line" className="h-5 w-5" />
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (!isOpen) return null;
  if (!mounted || typeof document === 'undefined') return null;
  return createPortal(overlayAndPanel, document.body);
}